import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

const CHUNK_RELOAD_MARKER_PREFIX = 'dice1k:chunk-reload';

function isRecoverableChunkError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const message = error.message || '';
  return (
    message.includes('Failed to fetch dynamically imported module')
    || message.includes('Importing a module script failed')
    || message.includes('error loading dynamically imported module')
    || message.includes('Unable to preload CSS')
  );
}

async function hardRefreshForChunkMismatch(marker: string): Promise<never> {
  try {
    sessionStorage.setItem(marker, '1');
  } catch {}

  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister().catch(() => undefined)));
    }
  } catch {}

  try {
    if ('caches' in window) {
      const cacheKeys = await caches.keys();
      await Promise.all(cacheKeys.map((cacheKey) => caches.delete(cacheKey).catch(() => false)));
    }
  } catch {}

  const nextUrl = new URL(window.location.href);
  nextUrl.searchParams.set('_v', `${Date.now()}`);
  window.location.replace(nextUrl.toString());

  return await new Promise<never>(() => undefined);
}

export function lazyWithReload<TProps>(
  importer: () => Promise<{ default: ComponentType<TProps> }>,
  key: string,
): LazyExoticComponent<ComponentType<TProps>> {
  return lazy(async () => {
    try {
      const module = await importer();
      try {
        sessionStorage.removeItem(`${CHUNK_RELOAD_MARKER_PREFIX}:${key}`);
      } catch {}
      return module;
    } catch (error) {
      if (typeof window !== 'undefined' && isRecoverableChunkError(error)) {
        const marker = `${CHUNK_RELOAD_MARKER_PREFIX}:${key}`;
        const alreadyRetried = (() => {
          try {
            return sessionStorage.getItem(marker) === '1';
          } catch {
            return false;
          }
        })();

        if (!alreadyRetried) {
          await hardRefreshForChunkMismatch(marker);
        }

        try {
          sessionStorage.removeItem(marker);
        } catch {}
      }

      throw error;
    }
  });
}